import { supabase } from '@/lib/supabase';

export interface ProductCategory {
  id: number;
  name: string;
  slug: string;
  sort_order: number;
}

export interface ProductItem {
  id: number;
  name: string;
  category_id: number;
  status: string;
  description: string | null;
  price: number | null;
  stock: number | null;
  media: { url: string; type: string }[] | null;
  slug: string | null;
  created_at: string;
}

export interface ProductCustomValue {
  id: number;
  product_id: number;
  field_id: number;
  value: string | null;
}

export interface ProductDetail {
  id: number;
  title: string;
  shortTitle: string;
  category: string;
  categoryId: number;
  slug: string;
  excerpt: string;
  summary: string;
  description: string;
  heroImage: string;
  cardImage: string;
  icon: string;
  features: string[];
  applications: string[];
  types: { name: string; description: string }[];
  specs: { label: string; value: string }[];
  faqs: { question: string; answer: string }[];
  status: string;
  price: number | null;
  stock: number | null;
}

async function getProductCategories(): Promise<ProductCategory[]> {
  const { data, error } = await supabase
    .from('product_categories')
    .select('id, name, sort_order')
    .order('sort_order');

  if (error) throw error;

  return (data || []).map((cat) => ({
    ...cat,
    slug: cat.name.toLowerCase().replace(/\s+/g, '-'),
  }));
}

function parseCustomValues(
  customValues: ProductCustomValue[]
): Record<string, string | null> {
  const map: Record<string, string | null> = {};
  const fieldMap: Record<number, string> = {
    1: 'excerpt',
    2: 'summary',
    3: 'icon',
    4: 'features',
    5: 'applications',
    6: 'types',
    7: 'specs',
    8: 'faqs',
    9: 'shortTitle',
  };

  for (const cv of customValues) {
    const fieldName = fieldMap[cv.field_id];
    if (fieldName) {
      map[fieldName] = cv.value;
    }
  }
  return map;
}

function parseArray(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return [];
  }
}

function parseTypes(value: string | null): { name: string; description: string }[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return [];
  }
}

function parseSpecs(value: string | null): { label: string; value: string }[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return [];
  }
}

function parseFaqs(value: string | null): { question: string; answer: string }[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return [];
  }
}

function mapProductToDetail(
  item: ProductItem,
  category?: ProductCategory
): ProductDetail {
  const customValues = (item as unknown as { product_custom_values?: ProductCustomValue[] }).product_custom_values || [];
  const values = parseCustomValues(customValues);

  const media = (item.media as unknown as { url: string; type: string }[]) || [];
  const heroImage = media[0]?.url || '';
  const cardImage = media[1]?.url || media[0]?.url || '';

  return {
    id: item.id,
    title: item.name,
    shortTitle: values.shortTitle || item.name,
    category: category?.name || 'Uncategorized',
    categoryId: item.category_id,
    slug: item.slug || String(item.id),
    excerpt: values.excerpt || item.description || '',
    summary: values.summary || item.description || '',
    description: item.description || '',
    heroImage,
    cardImage,
    icon: values.icon || 'ri-fire-line',
    features: parseArray(values.features),
    applications: parseArray(values.applications),
    types: parseTypes(values.types),
    specs: parseSpecs(values.specs),
    faqs: parseFaqs(values.faqs),
    status: item.status,
    price: item.price,
    stock: item.stock,
  };
}

export async function getProducts(): Promise<ProductDetail[]> {
  const { data, error } = await supabase
    .from('product_items')
    .select(`
      id, name, category_id, status, description, price, stock, media, slug,
      product_custom_values(field_id, value)
    `)
    .eq('status', 'active');

  if (error) throw error;

  const categories = await getProductCategories();

  return (data || []).map((item) => {
    const category = categories.find((c) => c.id === item.category_id);
    return mapProductToDetail(item as unknown as ProductItem, category);
  });
}

export async function getProductBySlug(slug: string): Promise<ProductDetail | null> {
  const { data, error } = await supabase
    .from('product_items')
    .select(`
      id, name, category_id, status, description, price, stock, media, slug,
      product_custom_values(field_id, value)
    `)
    .eq('slug', slug)
    .eq('status', 'active')
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const categories = await getProductCategories();
  const category = categories.find((c) => c.id === data.category_id);

  return mapProductToDetail(data as unknown as ProductItem, category);
}

export async function getProductById(id: string | number): Promise<ProductDetail | null> {
  const { data, error } = await supabase
    .from('product_items')
    .select(`
      id, name, category_id, status, description, price, stock, media, slug,
      product_custom_values(field_id, value)
    `)
    .eq('id', id)
    .eq('status', 'active')
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const categories = await getProductCategories();
  const category = categories.find((c) => c.id === data.category_id);

  return mapProductToDetail(data as unknown as ProductItem, category);
}

export { getProductCategories };